import React, { useEffect, useState, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Car, Plus, Trash2, Pencil, LogOut, Clock } from "lucide-react";
import { useLang } from "@/i18n";
import PageSeo from "@/components/PageSeo";
import { Field, TextInput, PrimaryBtn, GhostBtn, Overline } from "@/ui";
import { memberLogin, memberRegister, memberGoogleSession, memberGarage, memberAddCar, memberEditCar, memberDeleteCar } from "@/lib/api";
import { fmt } from "@/data/autosCatalog";

const GOLD = "#C7A668";
const TK = "tb_member";
const emptyCar = { make: "", model: "", year: "", color: "", nickname: "", plate: "", careNotes: "" };

export default function Garaje({ lang: langProp }) {
  const { L } = useLang(); // eslint-disable-line no-unused-vars
  const location = useLocation();
  const en = langProp === "en";
  const [token, setToken] = useState(localStorage.getItem(TK) || "");
  const [data, setData] = useState(null);
  const [booting, setBooting] = useState(true);
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [showCar, setShowCar] = useState(false);
  const [carForm, setCarForm] = useState(emptyCar);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async (t) => {
    try { const p = await memberGarage(t); setData(p); setToken(t); localStorage.setItem(TK, t); localStorage.setItem("tb_member_name", p?.member?.name || ""); }
    catch (e) { localStorage.removeItem(TK); setToken(""); setData(null); }
    setBooting(false);
  }, []);

  // Emergent Google Auth callback — process session_id first, then load.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  useEffect(() => {
    const hash = location.hash || "";
    if (hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
      window.history.replaceState(null, "", window.location.pathname);
      (async () => {
        try { const r = await memberGoogleSession(sid); await load(r.token); }
        catch (e) { setErr(en ? "Google sign-in failed" : "Falló el acceso con Google"); setBooting(false); }
      })();
      return;
    }
    if (token) load(token); else setBooting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + (en ? "/en/garage" : "/garaje");
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submitAuth = async () => {
    setErr(""); setBusy(true);
    try {
      const r = mode === "register"
        ? await memberRegister(form.name.trim(), form.email.trim(), form.password)
        : await memberLogin(form.email.trim(), form.password);
      await load(r.token);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const logout = () => { localStorage.removeItem(TK); localStorage.removeItem("tb_member_name"); setToken(""); setData(null); setMode("login"); };

  const saveCar = async () => {
    setBusy(true);
    try {
      if (editingId) await memberEditCar(token, editingId, carForm);
      else await memberAddCar(token, carForm);
      setShowCar(false); setCarForm(emptyCar); setEditingId(null); await load(token);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const editCar = (c) => { setCarForm({ ...emptyCar, ...c }); setEditingId(c.carId); setShowCar(true); };
  const delCar = async (id) => { try { await memberDeleteCar(token, id); await load(token); } catch (e) { console.error(e); } };

  const seo = <PageSeo esPath="/garaje" enPath="/en/garage" lang={en ? "en" : "es"} title={en ? "The Garage — Your Cars | Dukk" : "El Garaje — Tus Autos | Dukk"} description={en ? "Your Dukk Garage: register your cars, their care notes and see your full service history." : "Tu Garaje Dukk: registra tus autos, sus notas de cuidado y consulta tu historial de servicios."} />;

  if (booting) return (<div className="grain min-h-screen bg-[#0A0A0B] flex items-center justify-center"><span className="font-mono text-white/40 text-sm">…</span></div>);

  // ---- Auth gate ----
  if (!token || !data) {
    return (
      <>{seo}
        <div className="grain min-h-screen bg-[#0A0A0B] text-[#F2EFE8] flex items-center justify-center px-6 pt-24 pb-16">
          <Link to={en ? "/en/cars" : "/autos"} data-testid="garage-brand" className="no-underline font-display" style={{ position: "fixed", top: 22, left: 24, color: "#F2EFE8", fontSize: 20, letterSpacing: ".12em", fontWeight: 300 }}>DUKK</Link>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-md">
            <Overline gold>{en ? "The Garage" : "El Garaje"}</Overline>
            <h1 className="font-display text-3xl md:text-4xl text-white mt-3" style={{ fontWeight: 300 }}>{mode === "register" ? (en ? "Create your Garage" : "Crea tu Garaje") : (en ? "Enter your Garage" : "Entra a tu Garaje")}</h1>
            <p className="text-white/45 font-light mt-3">{en ? "Register your cars, their care notes and your full service history." : "Registra tus autos, sus notas de cuidado y tu historial de servicios."}</p>

            <button data-testid="garage-google-btn" onClick={googleLogin} className="w-full mt-8 flex items-center justify-center gap-3" style={{ background: "#F2EFE8", color: "#0a0a0a", fontWeight: 600, borderRadius: 8, padding: "13px 20px", fontSize: 14, border: "none", cursor: "pointer" }}>
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#4285F4" d="M45 24c0-1.6-.1-2.8-.4-4H24v7.5h12c-.2 2-1.6 5-4.6 7l7 5.4C42.8 42 45 34 45 24z"/><path fill="#34A853" d="M24 46c6 0 11-2 14.7-5.4l-7-5.4c-2 1.4-4.6 2.2-7.7 2.2-6 0-11-4-12.8-9.4l-7.2 5.6C4.6 40.6 13.6 46 24 46z"/><path fill="#FBBC05" d="M11.2 28c-.5-1.4-.8-2.9-.8-4.5s.3-3.1.8-4.5l-7.2-5.6C2.5 16.4 2 20 2 24s.5 7.6 2 12.1l7.2-5.6z"/><path fill="#EA4335" d="M24 10.4c3.4 0 6.4 1.2 8.8 3.4l6.2-6.2C35 3.9 30 2 24 2 13.6 2 4.6 7.4 1.8 15.9l7.2 5.6C11 14.4 16 10.4 24 10.4z"/></svg>
              {en ? "Continue with Google" : "Continuar con Google"}
            </button>

            <div className="flex items-center gap-3 my-6"><div className="h-px bg-white/12 flex-1" /><span className="font-mono text-xs text-white/30">{en ? "OR" : "O"}</span><div className="h-px bg-white/12 flex-1" /></div>

            <div className="space-y-4">
              {mode === "register" && <Field label={en ? "Full name" : "Nombre completo"} required><TextInput testid="garage-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>}
              <Field label={en ? "Email" : "Correo"} required><TextInput testid="garage-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
              <Field label={en ? "Password" : "Contraseña"} required><TextInput testid="garage-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Field>
              {err && <p data-testid="garage-error" className="text-sm" style={{ color: "#e08a8a" }}>{String(err)}</p>}
              <PrimaryBtn testid="garage-submit" gold disabled={busy} onClick={submitAuth}>{busy ? "…" : mode === "register" ? (en ? "Create account" : "Crear cuenta") : (en ? "Sign in" : "Entrar")}</PrimaryBtn>
            </div>
            <button data-testid="garage-toggle-mode" onClick={() => { setMode(mode === "register" ? "login" : "register"); setErr(""); }} className="mt-5 font-mono text-xs" style={{ color: GOLD, background: "none", border: "none", cursor: "pointer" }}>
              {mode === "register" ? (en ? "Already have an account? Sign in" : "¿Ya tienes cuenta? Entra") : (en ? "New here? Create your Garage" : "¿Nuevo? Crea tu Garaje")}
            </button>
          </motion.div>
        </div>
      </>
    );
  }

  // ---- Logged-in Garage ----
  const cars = data.cars || [];
  const requests = data.requests || [];
  return (
    <>{seo}
      <div className="grain min-h-screen bg-[#0A0A0B] text-[#F2EFE8] pt-28 pb-24 px-6">
        <Link to={en ? "/en/cars" : "/autos"} data-testid="garage-brand" className="no-underline font-display" style={{ position: "fixed", top: 22, left: 24, color: "#F2EFE8", fontSize: 20, letterSpacing: ".12em", fontWeight: 300 }}>DUKK</Link>
        <div className="max-w-5xl mx-auto">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Overline gold>{en ? "The Garage" : "El Garaje"}</Overline>
              <h1 className="font-display text-3xl md:text-4xl text-white mt-3" style={{ fontWeight: 300 }}>{en ? "Welcome" : "Bienvenido"}, {data.member?.name?.split(" ")[0] || ""}</h1>
            </div>
            <button data-testid="garage-logout" onClick={logout} className="font-mono text-xs flex items-center gap-2" style={{ color: "#9aa0a7", background: "none", border: "1px solid rgba(255,255,255,.15)", borderRadius: 6, padding: "8px 14px", cursor: "pointer" }}><LogOut size={13} /> {en ? "Sign out" : "Salir"}</button>
          </div>

          {/* Cars */}
          <div className="flex items-center justify-between mt-12 mb-5">
            <h2 className="font-mono text-sm tracking-widest uppercase text-white/50">{en ? "Your cars" : "Tus autos"}</h2>
            <button data-testid="garage-add-car" onClick={() => { setCarForm(emptyCar); setEditingId(null); setShowCar(true); setErr(""); }} className="font-mono text-xs flex items-center gap-2" style={{ color: "#0a0a0a", background: GOLD, borderRadius: 999, padding: "9px 18px", border: "none", cursor: "pointer", fontWeight: 600 }}><Plus size={14} /> {en ? "Add car" : "Agregar auto"}</button>
          </div>

          {cars.length === 0 && !showCar && <div className="rounded-sm border border-white/10 p-10 text-center text-white/40" style={{ background: "#101012" }}>{en ? "No cars yet. Add your first car to build your Garage." : "Aún no hay autos. Agrega tu primer auto para armar tu Garaje."}</div>}

          <div className="grid sm:grid-cols-2 gap-5">
            {cars.map((c) => (
              <div key={c.carId} data-testid={`garage-car-${c.carId}`} className="rounded-sm p-6" style={{ border: "1px solid rgba(199,166,104,.22)", background: "#121214" }}>
                <div className="flex items-start justify-between">
                  <Car size={22} color={GOLD} strokeWidth={1.4} />
                  <div className="flex gap-2">
                    <button data-testid={`garage-edit-${c.carId}`} onClick={() => editCar(c)} className="text-white/40 hover:text-white" style={{ background: "none", border: "none", cursor: "pointer" }}><Pencil size={15} /></button>
                    <button data-testid={`garage-delete-${c.carId}`} onClick={() => delCar(c.carId)} className="text-white/40 hover:text-red-400" style={{ background: "none", border: "none", cursor: "pointer" }}><Trash2 size={15} /></button>
                  </div>
                </div>
                <div className="font-display text-2xl text-white mt-3" style={{ fontWeight: 300 }}>{c.nickname || `${c.make} ${c.model}`}</div>
                <div className="text-white/50 text-sm mt-1">{[c.make, c.model, c.year, c.color].filter(Boolean).join(" · ")}</div>
                {c.plate && <div className="font-mono text-xs text-white/35 mt-2">{c.plate}</div>}
                {c.careNotes && <div className="mt-4 rounded-sm p-3 text-sm text-white/60" style={{ background: "rgba(199,166,104,.06)", border: "1px solid rgba(199,166,104,.15)" }}><span className="font-mono text-[10px] tracking-widest uppercase text-white/35 block mb-1">{en ? "Care notes" : "Notas de cuidado"}</span>{c.careNotes}</div>}
              </div>
            ))}
          </div>

          {showCar && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="rounded-sm p-6 mt-5" style={{ border: `1px solid ${GOLD}`, background: "#101012" }}>
              <h3 className="font-display text-xl text-white mb-4" style={{ fontWeight: 300 }}>{editingId ? (en ? "Edit car" : "Editar auto") : (en ? "Add a car" : "Agregar auto")}</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={en ? "Make" : "Marca"} required><TextInput testid="car-form-make" value={carForm.make} onChange={(e) => setCarForm({ ...carForm, make: e.target.value })} /></Field>
                <Field label={en ? "Model" : "Modelo"} required><TextInput testid="car-form-model" value={carForm.model} onChange={(e) => setCarForm({ ...carForm, model: e.target.value })} /></Field>
                <Field label={en ? "Year" : "Año"}><TextInput testid="car-form-year" value={carForm.year} onChange={(e) => setCarForm({ ...carForm, year: e.target.value })} /></Field>
                <Field label={en ? "Color" : "Color"}><TextInput testid="car-form-color" value={carForm.color} onChange={(e) => setCarForm({ ...carForm, color: e.target.value })} /></Field>
                <Field label={en ? "Nickname" : "Apodo"}><TextInput testid="car-form-nickname" value={carForm.nickname} onChange={(e) => setCarForm({ ...carForm, nickname: e.target.value })} /></Field>
                <Field label={en ? "Plate" : "Placas"}><TextInput testid="car-form-plate" value={carForm.plate} onChange={(e) => setCarForm({ ...carForm, plate: e.target.value })} /></Field>
              </div>
              <div className="mt-4"><Field label={en ? "Care notes (leather, ceramic, vinyl, what not to touch…)" : "Notas de cuidado (piel, cerámico, vinil, lo que no se toca…)"}><TextInput testid="car-form-notes" value={carForm.careNotes} onChange={(e) => setCarForm({ ...carForm, careNotes: e.target.value })} /></Field></div>
              {err && <p className="text-sm mt-3" style={{ color: "#e08a8a" }}>{String(err)}</p>}
              <div className="flex justify-end gap-3 mt-5">
                <GhostBtn onClick={() => { setShowCar(false); setEditingId(null); setErr(""); }}>{en ? "Cancel" : "Cancelar"}</GhostBtn>
                <PrimaryBtn testid="car-form-save" gold disabled={busy || !carForm.make || !carForm.model} onClick={saveCar}>{busy ? "…" : (en ? "Save" : "Guardar")}</PrimaryBtn>
              </div>
            </motion.div>
          )}

          {/* Service history */}
          <h2 className="font-mono text-sm tracking-widest uppercase text-white/50 mt-14 mb-5">{en ? "Service history" : "Historial de servicios"}</h2>
          {requests.length === 0 ? (
            <div className="rounded-sm border border-white/10 p-8 text-center text-white/40" style={{ background: "#101012" }}>{en ? "No services yet. Your Dukk requests will appear here." : "Aún no hay servicios. Tus solicitudes Dukk aparecerán aquí."}</div>
          ) : (
            <div className="space-y-3" data-testid="garage-history">
              {requests.map((r) => (
                <div key={r.requestId} className="rounded-sm p-5 flex items-center justify-between gap-4" style={{ border: "1px solid rgba(255,255,255,.1)", background: "#101012" }}>
                  <div>
                    <div className="text-white" style={{ fontWeight: 500 }}>{r.data?.bundle || r.data?.packageName || (en ? "Custom service" : "Servicio a medida")}</div>
                    <div className="font-mono text-xs text-white/40 flex items-center gap-2 mt-1"><Clock size={12} /> {(r.createdAt || "").slice(0, 10)} · {r.requestId}</div>
                  </div>
                  <div className="text-right">
                    {r.data?.estimatedTotalMXN ? <div style={{ color: GOLD, fontWeight: 500 }}>{fmt(r.data.estimatedTotalMXN)}</div> : null}
                    <div className="font-mono text-[10px] tracking-widest uppercase text-white/40 mt-1">{(r.status || "pending_validation").replace(/_/g, " ")}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
